import { Component, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { ButlerService } from "../../../../../services/api/butler/butler.service";
import { CheckboxModule } from "@openng/optimus-ui/checkbox";
import { RuleChain } from "../../../../../models/api/ruleChain";
import { first } from "rxjs";
import { TextareaModule } from "@openng/optimus-ui/textarea";

@Component({
  selector: "app-rule-chain",
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule, ButtonComponent, InputTextModule, TextareaModule, CheckboxModule],
  templateUrl: "./rule-chain.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./rule-chain.component.scss",
})
export class RuleChainComponent {
  private config = inject(DynamicDialogConfig);
  private ruleChain: RuleChain = this.config.data.ruleChain;

  public form = new FormGroup({
    label: new FormControl(this.ruleChain.label, Validators.required),
    notes: new FormControl(this.ruleChain.notes),
    active: new FormControl(this.ruleChain.active),
  });
  private ref = inject(DynamicDialogRef);
  private butlerService = inject(ButlerService);

  public requestStarted = signal(false);

  /**
   * Saves the RuleChain to the server, either creating a new one or editing an existing one.
   * If the RuleChain is new, it will also create an initial Instruction for it.
   * If the save is successful, it will close the dialog with true as the result.
   * If the save fails, it will show an error message and keep the dialog open.
   */
  public save() {
    if (this.form.valid) {
      if (!this.requestStarted()) {
        const ruleChainFormValues = this.form.value;
        const ruleChain: RuleChain = {
          ...ruleChainFormValues,
        };
        this.requestStarted.set(true);
        if (this.ruleChain.id) {
          ruleChain.id = this.ruleChain.id;
          this.butlerService.editRuleChain(ruleChain).pipe(first()).subscribe(this.afterServerResponse());
        } else {
          this.butlerService
            .createRuleChain(ruleChain)
            .pipe(first())
            .subscribe({
              next: (res) => {
                this.requestStarted.set(false);
                const ruleChainId = res.body?.id;
                if (ruleChainId) {
                  this.butlerService
                    .createInstruction({
                      ruleChain: { id: ruleChainId },
                      nodes: [],
                      notes: "Initial Instruction",
                    })
                    .pipe(first())
                    .subscribe(this.afterServerResponse());
                }
              },
              error: () => {
                this.requestStarted.set(false);
              },
            });
        }
      }
    }
  }

  /**
   * Handles the server response after attempting to save a RuleChain.
   * - `next`: Executes on successful response, clears the request state, and closes the dialog.
   * - `error`: Executes on error response, clears the request state, and displays an error message.
   */
  private afterServerResponse() {
    return {
      next: () => {
        this.requestStarted.set(false);
        this.ref.close(true);
      },
      error: () => {
        this.requestStarted.set(false);
      },
    };
  }
  /**
   * Closes the dialog with result `false`.
   */
  public closeDialog() {
    this.ref.close(false);
  }
}
